import type { BadgeProps } from '@clearpath/design-system/ui/badge';

/** The badge's own variant union, so these tables cannot drift from it. */
type BadgeVariant = NonNullable<BadgeProps['variant']>;

import {
  FederationDirection,
  FederationStatus,
  StepRunStatus,
} from "../__generated__/graphql";
import type {
} from "@clearpath/design-system";
import type { FederationInboxOrderFragment } from "../__generated__/graphql";
import { extractPositionLabels } from "@clearpath/shared-types/intake-position";

/**
 * Vendor-facing formatting helpers for the federation inbox.
 *
 * Two concerns live here so the row renderer stays declarative:
 *   1. Step-run status → human label + badge tone + "is this actionable?".
 *   2. Order products → dental case context (restoration, units, teeth,
 *      shade, material) surfaced so a vendor tech can prioritise work.
 */

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

/**
 * A step run is "actionable" when the vendor still has to DO something with
 * it. Terminal states (completed / superseded / cancelled / failed) are the
 * historical record — they must NOT inflate the "awaiting action" count, and
 * are hidden from the default inbox view (see FederationInboxPage filter).
 */
const TERMINAL_STATUSES: ReadonlySet<StepRunStatus> = new Set([
  StepRunStatus.Completed,
  StepRunStatus.Superseded,
  StepRunStatus.Cancelled,
  StepRunStatus.Failed,
]);

export function isActionable(status: StepRunStatus): boolean {
  return !TERMINAL_STATUSES.has(status);
}

const STATUS_LABELS: Record<StepRunStatus, string> = {
  [StepRunStatus.Pending]: "New",
  [StepRunStatus.PendingVendor]: "Awaiting pickup",
  [StepRunStatus.Ready]: "Ready",
  [StepRunStatus.InProgress]: "In progress",
  [StepRunStatus.OnHold]: "On hold",
  [StepRunStatus.Completed]: "Completed",
  [StepRunStatus.Superseded]: "Superseded",
  [StepRunStatus.Cancelled]: "Cancelled",
  [StepRunStatus.Failed]: "Failed",
};

const STATUS_TONES: Record<StepRunStatus, BadgeVariant> = {
  [StepRunStatus.Pending]: "info",
  [StepRunStatus.PendingVendor]: "info",
  [StepRunStatus.Ready]: "default",
  [StepRunStatus.InProgress]: "warning",
  [StepRunStatus.OnHold]: "warning",
  [StepRunStatus.Completed]: "success",
  [StepRunStatus.Superseded]: "neutral",
  [StepRunStatus.Cancelled]: "neutral",
  [StepRunStatus.Failed]: "destructive",
};

export function statusLabel(status: StepRunStatus): string {
  return STATUS_LABELS[status] ?? String(status);
}

export function statusTone(status: StepRunStatus): BadgeVariant {
  return STATUS_TONES[status] ?? "neutral";
}

// ---------------------------------------------------------------------------
// Federation link enums (Connections page)
// ---------------------------------------------------------------------------

const DIRECTION_LABELS: Record<FederationDirection, string> = {
  [FederationDirection.Inbound]: "Incoming",
  [FederationDirection.Outbound]: "Outgoing",
  [FederationDirection.Bidirectional]: "Two-way",
};

export function directionLabel(direction: FederationDirection): string {
  return DIRECTION_LABELS[direction] ?? titleCaseEnum(direction);
}

const FED_STATUS_LABELS: Record<FederationStatus, string> = {
  [FederationStatus.Active]: "Active",
  [FederationStatus.Pending]: "Pending",
  [FederationStatus.Suspended]: "Suspended",
  [FederationStatus.Expired]: "Expired",
  [FederationStatus.Revoked]: "Revoked",
};

const FED_STATUS_TONES: Record<FederationStatus, BadgeVariant> = {
  [FederationStatus.Active]: "success",
  [FederationStatus.Pending]: "warning",
  [FederationStatus.Suspended]: "warning",
  [FederationStatus.Expired]: "neutral",
  [FederationStatus.Revoked]: "destructive",
};

export function federationStatusLabel(status: FederationStatus): string {
  return FED_STATUS_LABELS[status] ?? titleCaseEnum(status);
}

export function federationStatusTone(status: FederationStatus): BadgeVariant {
  return FED_STATUS_TONES[status] ?? "neutral";
}

/**
 * Humanise a raw work-type token (e.g. `SCAN_REVIEW`, `cad_design`) into a
 * label a vendor tech reads — "Scan Review", "CAD Design". Known dental
 * acronyms stay upper-cased; everything else is Title Cased.
 */
const WORK_TYPE_ACRONYMS = new Set(["CAD", "CAM", "QC", "STL", "PFM"]);

export function workTypeLabel(raw: string): string {
  const cleaned = raw.trim();
  if (cleaned === "") return raw;
  return cleaned
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => {
      const upper = word.toUpperCase();
      if (WORK_TYPE_ACRONYMS.has(upper)) return upper;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function titleCaseEnum(raw: string): string {
  return workTypeLabel(raw);
}

/**
 * Generic UPPER_SNAKE / kebab enum → human label, for order type / status
 * tokens that would otherwise leak to the vendor as raw enums
 * (e.g. `FEDERATED_INCOMING` → "Federated Incoming", `IN_PROGRESS` →
 * "In Progress").
 */
export function humanizeEnum(raw: string | null | undefined): string {
  if (!raw) return "—";
  return titleCaseEnum(raw);
}

// ---------------------------------------------------------------------------
// Case / clinical context
// ---------------------------------------------------------------------------

export interface CaseContext {
  /** e.g. "Zirconia Crown ×2" — restoration type + total unit count. */
  restoration: string | null;
  /** Tooth numbers pulled from intake `selected_teeth`, e.g. ["14","15"]. */
  teeth: string[];
  shade: string | null;
  material: string | null;
}

type IntakeRecord = Record<string, unknown>;

function parseIntake(raw: unknown): IntakeRecord | null {
  if (!raw) return null;
  let obj: unknown = raw;
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;
  const rec = obj as IntakeRecord;
  return Object.keys(rec).length === 0 ? null : rec;
}

function pickString(intake: IntakeRecord | null, ...keys: string[]): string | null {
  if (!intake) return null;
  for (const key of keys) {
    const v = intake[key];
    if (typeof v === "string" && v.trim() !== "") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
  }
  return null;
}

// Positions for the vendor case summary now come from the ONE shared extractor
// (@clearpath/shared-types/intake-position), the same key list + precedence the
// in-house work slip reads. This copy used to read only the PLURAL array keys
// (selected_teeth / teeth / tooth_numbers) and never the SINGULAR `tooth_number`
// that migration 0160 sweeps positions into — so a case outsourced to a vendor
// rendered ZERO positions on the bench slip (ENG-641). Delegating also gains the
// array-OR-scalar coercion and the first-NON-EMPTY-wins precedence, so a key
// present-but-empty no longer short-circuits and discards a real answer.
function pickTeeth(intake: IntakeRecord | null): string[] {
  return intake ? extractPositionLabels(intake) : [];
}

/**
 * Derive the clinical case summary for an order from its line items.
 * Reads `intakeFormResponse` first (structured), then `customFields`.
 */
export function caseContext(order: FederationInboxOrderFragment): CaseContext {
  const products = order.orderProducts ?? [];

  const teeth: string[] = [];
  let shade: string | null = null;
  let material: string | null = null;
  const restorationParts: string[] = [];

  for (const p of products) {
    const intake = parseIntake(p.intakeFormResponse) ?? parseIntake(p.customFields);
    for (const t of pickTeeth(intake)) {
      if (!teeth.includes(t)) teeth.push(t);
    }
    if (!shade) shade = pickString(intake, "shade");
    if (!material) material = pickString(intake, "material");

    const name = p.productType?.name ?? null;
    if (name) {
      restorationParts.push(p.quantity > 1 ? `${name} ×${p.quantity}` : name);
    }
  }

  return {
    restoration: restorationParts.length > 0 ? restorationParts.join(", ") : null,
    teeth,
    shade,
    material,
  };
}
